import { Card, Form, Input, Button, Space, Select, Row, Col, Upload, Divider } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {Edit, useDataProvider, useNotify, useQuery, useEditContext} from "react-admin";
import { ExtendedDataProvider } from "@src/types";
import React, { useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import MonacoEditorWrapper from "@src/components/Common/MonacoEditorWrapper";

const { Option } = Select;

const DocumentCreateForm = (props: any) => {
    const [form] = Form.useForm();
    const notify = useNotify();
    const history = useHistory();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const {record, loading, loaded} = useEditContext();

    React.useEffect(() => {
        if (record && Object.keys(record).length > 0) {
            form.setFieldsValue({
                ...record,
                service: record.service?.id || record.service
            });
        }
    }, [record, form]);

    const location = useLocation();
    const [saving, setSaving] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const [fileSize, setFileSize] = useState<number>(0);
    const resource = "serviceowner_pack_documents";
    const title = "Document";

    const contentValue = Form.useWatch('raw_content', form);
    const charCount = contentValue?.length || 0;

    // Get service ID from URL query params (supports both 'service' and 'service_id')
    const searchParams = new URLSearchParams(location.search);
    const serviceIdRaw = searchParams.get('service_id') || searchParams.get('service');
    const serviceIdFromUrl = serviceIdRaw && !isNaN(parseInt(serviceIdRaw)) ? parseInt(serviceIdRaw) : undefined;

    // Fetch services list for the dropdown (only if service ID is not provided)
    const { data: services, loaded: servicesLoaded } = useQuery({
        type: 'getList',
        resource: 'services',
        payload: {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: {}
        }
    }, {
        enabled: !serviceIdFromUrl // Only fetch if we need to show the dropdown
    });

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleFileUpload = (file: File) => {
        setFileSize(file.size);
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            form.setFieldsValue({
                filename: file.name,
                raw_content: content
            });
            notify('File uploaded and content loaded successfully');
        };
        reader.onerror = () => {
            notify('Failed to read file', 'error');
        };
        reader.readAsText(file);
        return false;
    };

    const handleSubmitError = (error: any) => {
        // DRF-style errors like {"detail": "A document with the same content already exists..."}
        if (error.body?.detail) {
            notify("" + error.body.detail, 'error');
            return;
        }
        if (error.status == 400 && error.body) {
            for (const [key, value] of Object.entries<any>(error.body))
                form.setFields([{ name: key, errors: Array.isArray(value) ? value : ["" + value] }])
            if ("non_field_errors" in error.body) {
                notify("" + error.body['non_field_errors'], 'error')
            }
        } else {
            notify(error.message || `Failed to save ${title.toLowerCase()}`, 'error');
        }
    };

    const handleSubmit = async (values: any) => {
        setSaving(true);
        if(record) {
            if (values.raw_content === record.raw_content) {
                notify('Content is not changed', 'warning');
                setSaving(false);
                return;
            }
            dataProvider.update(resource, { id: record.id, data: values, previousData: { id: record.id } })
                .then(() => {
                    notify(`${title} updated successfully`);
                    history.goBack();
                })
                .catch(handleSubmitError)
                .finally(() => {
                    setSaving(false);
                })
        } else
            dataProvider.createAction(resource, {
                id: undefined,
                verb: `service/${values.service}/`,
                data: {
                    filename: values.filename,
                    raw_content: values.raw_content
                }
            }).then(() =>{
                notify('Vector document created successfully');
                history.goBack();
            }).catch(handleSubmitError)
            .finally(() => {
                setSaving(false);
            })

    };

    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
            style={{margin: "10px"}}
            className="row-col"
            labelAlign={'left'}
            initialValues={{
                service: serviceIdFromUrl ? serviceIdFromUrl : undefined
            }}
        >
            {serviceIdFromUrl && (
                <Form.Item
                    name="service"
                    hidden
                    rules={[{ required: true }]}
                >
                    <Input type="hidden" />
                </Form.Item>
            )}

            <Row gutter={16}>
                <Col span={!serviceIdFromUrl ? 12 : 24}>
                    <Form.Item
                        label="Filename"
                        name="filename"
                        rules={[{ required: true, message: 'Please enter a filename' }]}
                        labelCol={{ span: !serviceIdFromUrl ? 4 : 4 }}
                        wrapperCol={{ span: !serviceIdFromUrl ? 20 : 20 }}
                    >
                        <Input placeholder="e.g., documentation.txt" />
                    </Form.Item>
                </Col>
                {!serviceIdFromUrl && (
                    <Col span={12}>
                        <Form.Item
                            label="Service"
                            name="service"
                            rules={[{ required: true, message: 'Please select a service' }]}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                        >
                            <Select
                                placeholder="Select a service"
                                showSearch
                                optionFilterProp="children"
                                loading={!servicesLoaded}
                                allowClear
                            >
                                {services?.map((service: any) => (
                                    <Option key={service.id} value={service.id}>
                                        {service.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                )}
            </Row>

            <Form.Item
                label="Content"
                name="raw_content"
                rules={[{ required: true, message: 'Please enter the content' }]}
                labelCol={{ span: 2 }}
                wrapperCol={{ span: 22 }}
            >
                <MonacoEditorWrapper />
            </Form.Item>
            {charCount > 0 && (
                <Form.Item
                    labelCol={{ span: 2 }}
                    wrapperCol={{ span: 22, offset: 2 }}
                >
                    <div style={{ marginTop: '-16px', color: '#888', fontSize: '12px' }}>
                        {charCount.toLocaleString()} characters
                    </div>
                </Form.Item>
            )}

            <Divider>or</Divider>

            <Form.Item
                label="Upload File"
                labelCol={{ span: 2 }}
                wrapperCol={{ span: 22 }}
            >
                <Upload
                    fileList={fileList}
                    beforeUpload={handleFileUpload}
                    onRemove={() => {
                        setFileList([]);
                        setFileSize(0);
                    }}
                    onChange={({ fileList: newFileList }) => setFileList(newFileList)}
                    maxCount={1}
                    accept=".txt,.md,.json,.xml,.csv,.log"
                >
                    <Button icon={<UploadOutlined />}>
                        Click to Upload Text File
                    </Button>
                </Upload>
                <div style={{ marginTop: '8px', color: '#888', fontSize: '12px' }}>
                    Upload a text file to automatically populate filename and content
                    {fileSize > 0 && (
                        <span style={{ marginLeft: '8px' }}>
                            (File size: {formatFileSize(fileSize)})
                        </span>
                    )}
                </div>
            </Form.Item>

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        {record ? "Update Document" : "Create Document"}
                    </Button>
                    <Button onClick={() => history.goBack()}>
                        Cancel
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
}



const DocumentCreate = (props: any) => {
    return (
        <>
            <Card
                className=" header-solid h-full ant-card pt-0"
                bordered={false}
                title={props.id ? "Update Document" : "Create Document"}
            >
                {props.id &&
                    <Edit {...props}>
                        <DocumentCreateForm />
                    </Edit>
                }
                {!props.id && <DocumentCreateForm />}
            </Card>
        </>
    )
}

export default DocumentCreate;